import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type {
  CARDS_ASPECT_RATIO,
  CardContentTypeT,
  CardSizeT,
  CardTypeT,
} from 'blocks/Cards/Card.h'
import type { COLOR } from 'vars/index'

import React, { useEffect, useRef, useState } from 'react'
import {
  ButtonCorners,
  ButtonFluid,
  ButtonIconSizes,
  IconPositions,
} from 'atoms/Button/Button.h'
import { SecondaryOpacityFontButton1Button } from 'atoms/Button/Templates'
import { IconNames } from 'atoms/Icon'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { CardType } from 'blocks/Cards/Card.h'
import {
  CardDimensions,
  useSimpleVisibleCards,
} from 'containers/RowContainer/RowSimpleContainer/hooks/useSimpleVisibleCards'
import { FIXED_INDENTS, GRID, INDENTS, LAYOUT } from 'vars/index'
import { LazyLoad } from './components/LazyLoad/LazyLoad'
import { GridColumnGaps, GridRowGaps } from './Grid.h'
import { GridCollapseToggleSC, GridSC, GridWrapper } from './Grid.styled'
import { GridCollapseProvider } from './gridCollapseContext'
import { useClientGridWidth } from './hooks/useClientGridWidth'

export interface ISimpleGridProps {
  children: React.ReactNode
  content: object[]
  rowGap?: (typeof GridRowGaps)[keyof typeof GridRowGaps]
  columnGap?: (typeof GridColumnGaps)[keyof typeof GridColumnGaps]
  title?: ITextBoxProps['title']
  colorTitle?: ITextBoxProps['color']
  fontTitle?: ITextBoxProps['font']
  maxLineTitle?: ITextBoxProps['maxLines']
  paddings?: Array<FIXED_INDENTS | LAYOUT | GRID>
  marginTitle?: Array<FIXED_INDENTS | LAYOUT | GRID>
  onLoadMoreItems: () => any
  aspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  verticalTopOffset?: LAYOUT
  backgroundColor?: COLOR
  contentType: CardContentTypeT
  type: CardTypeT
  size: CardSizeT
  dataTestId?: string
  /**
   * FP-4238: offer a Show More / Show Less toggle when the section holds more
   * items than fit into the collapsed state (UI-7.6.1, UI-7.6.2).
   * `HORIZONTAL_SEARCH` grids default to collapsible (FP-4278): Era-served
   * skins often drop the boolean `data` flag, so search sections must not
   * depend on it.
   */
  collapsible?: boolean
  showMoreTitle?: string
  showLessTitle?: string
  /**
   * Identity of the result set behind the grid. Changing it collapses the grid
   * again, so every new result set starts collapsed even when it happens to
   * produce the same section with the same number of items.
   */
  collapseResetKey?: string | number | null
  /**
   * FP-4260: render the section as the recommendations grid of the content
   * details page (Figma `16284:249978`), which carries its own paddings, gaps,
   * title gap and column ladder. Opt-in, so every other grid keeps its metrics.
   */
  detailsGrid?: boolean
}

/**
 * FP-4278 QA matrix (comment 233426) plus FP-4296:
 * - Show More / Show Less only for 0–1239px (`MEDIA.MAX_1239`). Above that
 *   the section renders every item and the toggle is hidden.
 * - 1 column (<600px): 4 stacked cards, then Show More
 * - 2–3 columns: two rows of the live grid (600→4, 768–1239→6)
 */
const COLLAPSED_ROWS = 2
const MOBILE_STACK_COLUMNS = 1
const MOBILE_STACK_ITEMS = 4
/** Matches `MEDIA.MAX_1239`: no collapse / no toggle from 1240px up (FP-4296). */
const COLLAPSE_MAX_WIDTH = 1239
/** Matches `MEDIA.MAX_767`: Show More spans the grid; hug + right-align above. */
const FULL_WIDTH_TOGGLE_MAX = 767

const DETAILS_GRID_PADDINGS = [
  GRID.VerticalPaddingsDetailsRecommendations,
  GRID.HorizontalPaddingsDetailsRecommendations,
]
const DETAILS_GRID_MARGIN_TITLE = [
  FIXED_INDENTS.INDENT_0,
  FIXED_INDENTS.INDENT_0,
  GRID.TitleGapDetailsRecommendations,
]
/**
 * FP-4260: 768-1239 is the one stop where the recommendations grid parts from
 * the shared `HORIZONTAL`/`M` ladder — the concept (Figma `16284:251296`)
 * renders 3 columns there against the 2 a content row shows at that width.
 * Every other stop already matches, so the row table stays untouched.
 */
const DETAILS_GRID_TABLET_COLUMNS = 3

/**
 * Number of leading children a collapsed grid shows, or `null` when the grid
 * should not collapse at all.
 *
 * Follow `visibleCards` so the count matches the live layout (FP-4279 columns).
 */
export const getCollapsedItemCount = ({
  itemCount,
  visibleCards,
  collapsible,
}: {
  itemCount: number
  visibleCards: number
  collapsible?: boolean
}): number | null => {
  if (!collapsible || visibleCards <= 0) return null

  // `visibleCards` is fractional for the card types that deliberately show a
  // partial trailing card; only whole cards make up a collapsed row.
  const columns = Math.max(1, Math.floor(visibleCards))
  const collapsedCount =
    columns <= MOBILE_STACK_COLUMNS
      ? MOBILE_STACK_ITEMS
      : columns * COLLAPSED_ROWS

  return itemCount > collapsedCount ? collapsedCount : null
}

export const SimpleGrid = ({
  children,
  content,
  rowGap = GridRowGaps.DEFAULT,
  columnGap = GridColumnGaps.DEFAULT,
  title,
  colorTitle,
  fontTitle = TextBoxTypes.Headline3,
  maxLineTitle = 1,
  verticalTopOffset,
  paddings = [FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings],
  marginTitle = [FIXED_INDENTS.INDENT_0],
  onLoadMoreItems,
  aspectRatio,
  backgroundColor,
  contentType,
  size,
  type,
  dataTestId,
  collapsible,
  showMoreTitle,
  showLessTitle,
  collapseResetKey,
  detailsGrid = false,
}: ISimpleGridProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const isCollapsible = collapsible ?? type === CardType.HORIZONTAL_SEARCH
  const moreTitle = showMoreTitle ?? 'Show More'
  const lessTitle = showLessTitle ?? 'Show Less'

  const clientWidth = useClientGridWidth()

  const rowVisibleCards = useSimpleVisibleCards({
    clientWidth,
    contentType,
    size,
    type,
  })
  const visibleCards =
    detailsGrid &&
    clientWidth >= CardDimensions.TABLET_S &&
    clientWidth < CardDimensions.TABLET_L
      ? DETAILS_GRID_TABLET_COLUMNS
      : rowVisibleCards

  // skin `map` keys its children by index, so this grid instance is reused when
  // the list behind it changes (e.g. a new search) rather than remounting.
  // Collapse it again whenever the grid shows a different list (UI-7.6.1).
  //
  // `collapseResetKey` carries the identity of the result set, because the
  // `content` reference cannot be used here: skin data resolves through
  // `useRaw`, which rebuilds arrays on every call for non-store sources, so
  // depending on it would collapse the grid on each render.
  const contentLength = content?.length
  useEffect(() => {
    if (isCollapsible) setExpanded(false)
  }, [collapseResetKey, contentLength, isCollapsible, title])

  const items = React.Children.toArray(children)
  // Search skins pass one `map` child that expands into cards. Count the
  // section payload so Show More is not skipped when `children.length === 1`.
  const itemCount = Math.max(items.length, contentLength ?? 0)
  const collapsedItemCount = getCollapsedItemCount({
    itemCount,
    visibleCards,
    collapsible:
      isCollapsible && clientWidth > 0 && clientWidth <= COLLAPSE_MAX_WIDTH,
  })
  const collapsed = collapsedItemCount != null
  const canSliceChildren =
    collapsed && !expanded && items.length > collapsedItemCount
  const visibleItems = canSliceChildren
    ? items.slice(0, collapsedItemCount)
    : items
  const mappedItemLimit =
    collapsed && !expanded && !canSliceChildren ? collapsedItemCount : null

  return visibleCards &&
    content?.length > 0 &&
    React.Children.count(children) ? (
    <GridCollapseProvider value={mappedItemLimit}>
      <GridWrapper
        paddings={detailsGrid ? DETAILS_GRID_PADDINGS : paddings}
        marginTitle={detailsGrid ? DETAILS_GRID_MARGIN_TITLE : marginTitle}
        verticalTopOffset={verticalTopOffset}
        backgroundColor={backgroundColor}
      >
        {title && (
          <TextBox
            color={colorTitle}
            maxLines={maxLineTitle}
            font={fontTitle}
            title={title}
          />
        )}
        <GridSC
          ref={gridRef}
          rowGap={detailsGrid ? GridRowGaps.DETAILS_RECOMMENDATIONS : rowGap}
          columnGap={
            detailsGrid ? GridColumnGaps.DETAILS_RECOMMENDATIONS : columnGap
          }
          horizontalPaddings={
            detailsGrid
              ? GRID.HorizontalPaddingsDetailsRecommendations
              : undefined
          }
          cardsCount={visibleCards}
          aspectRatio={aspectRatio!}
        >
          <LazyLoad
            onLoadMore={onLoadMoreItems}
            percentToTrigger={50}
            dataTestId={dataTestId}
          >
            {visibleItems}
          </LazyLoad>
        </GridSC>
        {collapsed && (
          <GridCollapseToggleSC>
            <SecondaryOpacityFontButton1Button
              title={expanded ? lessTitle : moreTitle}
              icon={expanded ? IconNames.angleUp : IconNames.angleDown}
              iconPosition={IconPositions.Right}
              iconSize={ButtonIconSizes.mSm}
              corners={ButtonCorners.CORNERS_50}
              fluid={
                clientWidth > 0 && clientWidth <= FULL_WIDTH_TOGGLE_MAX
                  ? ButtonFluid.Horizontal
                  : ButtonFluid.None
              }
              paddings={[
                INDENTS.INDENT_30,
                INDENTS.INDENT_100,
                INDENTS.INDENT_30,
                INDENTS.INDENT_100,
              ]}
              onClick={() => setExpanded((value) => !value)}
              dataTestId='grid-collapse-toggle'
            />
          </GridCollapseToggleSC>
        )}
      </GridWrapper>
    </GridCollapseProvider>
  ) : (
    <></>
  )
}
